import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./SpecialistProfilePage.css";

type ReferralMethodVM = {
  referralMethodId: number;
  code: string;
  name: string;
  notes: string | null;
};

type ReferralFormVM = {
  hasForm: boolean;
  specialistReferralFormId: number | null;
  fileName: string | null;
  contentType?: string | null;
  fileSizeBytes?: number | null;
  uploadedAt?: string | null;
  uploadedBy?: string | null;
};

type SpecialistApiResponse = {
  specialistId?: number;

  fullName?: string;
  gender?: string | null;

  // legacy raw ageGroup still exists
  ageGroup?: string | null;

  // NEW fields from API
  acceptedAgeGroups?: string[] | null;
  acceptedAgeGroupsLabel?: string | null;

  acceptingNewReferrals?: boolean;

  specialty?: { specialtyId?: number; name?: string | null } | null;

  clinic?: {
    clinicId?: number;
    name?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    region?: string | null;
    postalCode?: string | null;

    phone?: string | null;

    fax?: string | null;
    administrationEmail?: string | null;
  } | null;

  waitTime?: {
    routineWeeks?: number | null;
    urgentWeeks?: number | null;
    capturedAt?: string | null;
  } | null;

  availability?: string | null;

  otherNotes?: string | null;

  languages?: any[] | string[] | null;
  visitTypes?: any[] | string[] | null;
  coverage?: any[] | string[] | null;
  hospitals?: any[] | string[] | null;

  // Backward compat: conditions = accepted
  conditions?: any[] | string[] | null;

  // NEW split sets
  acceptedConditions?: any[] | string[] | null;
  excludedConditions?: any[] | string[] | null;

  procedures?: any[] | string[] | null;
  requiredInvestigations?: any[] | string[] | null;

  // NEW referral fields
  referralMethods?: ReferralMethodVM[] | null;
  referralForm?: ReferralFormVM | null;
};

const extractNameList = (arr: any[] | string[] | undefined | null): string[] => {
  if (!arr || !Array.isArray(arr)) return [];
  return arr
    .map((x) => {
      if (typeof x === "string") return x;
      if (x?.name) return x.name;
      if (x?.Name) return x.Name;
      return null;
    })
    .filter((s): s is string => typeof s === "string" && s.trim().length > 0);
};

const formatAddress = (clinic: SpecialistApiResponse["clinic"]) => {
  if (!clinic) return null;

  const a1 =
    typeof clinic.addressLine1 === "string" ? clinic.addressLine1.trim() : "";
  const a2 =
    typeof clinic.addressLine2 === "string" ? clinic.addressLine2.trim() : "";
  const city = typeof clinic.city === "string" ? clinic.city.trim() : "";
  const region = typeof clinic.region === "string" ? clinic.region.trim() : "";
  const pc =
    typeof clinic.postalCode === "string" ? clinic.postalCode.trim() : "";

  const line = [a1, a2].filter(Boolean).join(", ");
  const locality = [city, region].filter(Boolean).join(", ");
  const parts = [line, locality, pc].filter(Boolean);

  return parts.length ? parts.join(" • ") : null;
};

const fmtWeeks = (w: number | null) => {
  if (w === null) return "—";
  if (w === 1) return "1 week";
  return `${w} weeks`;
};

const cleanStr = (v: any): string | null => {
  if (typeof v !== "string") return null;
  const s = v.trim();
  return s.length ? s : null;
};

const iconForReferralCode = (code: string) => {
  const c = (code || "").toUpperCase();
  if (c === "FAX") return "📠";
  if (c === "EREFERRAL") return "🌐";
  if (c === "PORTAL") return "🔗";
  if (c === "REFFORM") return "📄";
  if (c === "PHONE") return "☎️";
  return "➡️";
};

const SpecialistProfilePage: React.FC = () => {
  const { id } = useParams();
  const specialistId = id ? parseInt(id, 10) : NaN;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SpecialistApiResponse | null>(null);

  // NEW: referral form open state
  const [openingForm, setOpeningForm] = useState(false);
  const [openFormError, setOpenFormError] = useState<string | null>(null);

  // ✅ Minimal change: back route now points to portal family doctor specialist search
  const BACK_TO_SEARCH = "/portal/family-doctor/find-specialist";

  useEffect(() => {
    if (!id || Number.isNaN(specialistId)) {
      setLoading(false);
      setError("Invalid specialist ID.");
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`/api/specialists/${specialistId}`);
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `Request failed (${res.status})`);
        }

        const json = await res.json();
        setData(json);
      } catch (e: any) {
        setError(e?.message || "Failed to load specialist profile.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, specialistId]);

  const vm = useMemo(() => {
    if (!data) return null;

    const accepting = data.acceptingNewReferrals === true;

    const acceptedAgeGroups = Array.isArray(data.acceptedAgeGroups)
      ? data.acceptedAgeGroups
      : [];
    const acceptedAgeGroupsLabel =
      cleanStr(data.acceptedAgeGroupsLabel) ||
      (acceptedAgeGroups.length > 0 ? acceptedAgeGroups.join(", ") : null);

    const acceptedConditions = extractNameList(
      (data.acceptedConditions as any[]) ?? (data.conditions as any[])
    );
    const excludedConditions = extractNameList(data.excludedConditions as any[]);

    const clinicFax = cleanStr(data.clinic?.fax);
    const clinicAdminEmail = cleanStr(data.clinic?.administrationEmail);

    const referralMethods: ReferralMethodVM[] = Array.isArray(data.referralMethods)
      ? data.referralMethods
          .filter((m: any) => m && typeof m.name === "string")
          .map((m: any) => ({
            referralMethodId: Number(m.referralMethodId),
            code: String(m.code || ""),
            name: String(m.name || ""),
            notes: cleanStr(m.notes),
          }))
      : [];

    const referralForm: ReferralFormVM | null =
      data.referralForm && typeof data.referralForm === "object"
        ? {
            hasForm: !!(data.referralForm as any).hasForm,
            specialistReferralFormId:
              (data.referralForm as any).specialistReferralFormId ?? null,
            fileName: cleanStr((data.referralForm as any).fileName),
            contentType: cleanStr((data.referralForm as any).contentType),
            fileSizeBytes: (data.referralForm as any).fileSizeBytes ?? null,
            uploadedAt: cleanStr((data.referralForm as any).uploadedAt),
            uploadedBy: cleanStr((data.referralForm as any).uploadedBy),
          }
        : null;

    return {
      name: data.fullName || "Unnamed Specialist",
      specialty: data.specialty?.name || "Specialty not specified",
      gender: data.gender || null,

      accepting,
      acceptanceLabel: accepting
        ? "Accepting new referrals"
        : "Not accepting new referrals",
      acceptanceTone: accepting ? "ok" : "no",

      availability: data.availability || null,

      acceptedAgeGroupsLabel,

      clinicName: data.clinic?.name || null,
      clinicPhone: cleanStr(data.clinic?.phone),
      clinicFax,
      clinicAdminEmail,
      address: formatAddress(data.clinic),

      routineWeeks: data.waitTime?.routineWeeks ?? null,
      urgentWeeks: data.waitTime?.urgentWeeks ?? null,

      otherNotes: cleanStr(data.otherNotes),

      languages: extractNameList(data.languages as any[]),
      visitTypes: extractNameList(data.visitTypes as any[]),
      coverage: extractNameList(data.coverage as any[]),
      hospitals: extractNameList(data.hospitals as any[]),

      acceptedConditions,
      excludedConditions,

      procedures: extractNameList(data.procedures as any[]),
      investigations: extractNameList(data.requiredInvestigations as any[]),

      referralMethods,
      referralForm,
    };
  }, [data]);

  const ChipRow = ({ items }: { items: string[] }) => (
    <div className="sp-chips">
      {items.map((t) => (
        <span key={t} className="sp-chip">
          {t}
        </span>
      ))}
    </div>
  );

  // NEW: open referral form using signed link endpoint
  const handleOpenReferralForm = async () => {
    if (!vm?.referralForm?.hasForm) return;

    try {
      setOpeningForm(true);
      setOpenFormError(null);

      const res = await fetch(
        `/api/specialists/${specialistId}/referral-form-link`
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const json = await res.json();
      const url = json?.url;

      if (!url || typeof url !== "string") {
        throw new Error("Referral form link is missing.");
      }

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setOpenFormError(e?.message || "Unable to open the referral form.");
    } finally {
      setOpeningForm(false);
    }
  };

  return (
    <div className="sp-page">
      <div className="sp-topbar">
        <div className="sp-container sp-topbar-inner">
          {/* ✅ Minimal change */}
          <Link className="sp-back" to={BACK_TO_SEARCH}>
            <span className="sp-back-arrow">←</span> Back to search
          </Link>
        </div>
      </div>

      <header className="sp-hero">
        <div className="sp-container sp-hero-inner">
          <div className="sp-hero-main">
            <div className="sp-hero-kicker">Specialist profile</div>

            <div className="sp-title-row">
              <h1 className="sp-title">
                {loading ? "Loading…" : vm?.name || "Specialist"}
              </h1>

              {!loading && !error && vm && (
                <span className={`sp-pill sp-pill-${vm.acceptanceTone}`}>
                  {vm.acceptanceLabel}
                </span>
              )}
            </div>

            <div className="sp-subtitle">
              <span className="sp-subtitle-strong">
                {vm?.specialty || "Specialty"}
              </span>
              {vm?.gender && <span className="sp-dot">•</span>}
              {vm?.gender && <span>{vm.gender}</span>}
            </div>

            {!loading && !error && vm?.availability && (
              <div className="sp-inline-note">
                <span className="sp-inline-note-label">Availability:</span>{" "}
                {vm.availability}
              </div>
            )}
          </div>

          {(vm?.routineWeeks !== null || vm?.urgentWeeks !== null) && (
            <div className="sp-hero-badges">
              <div className="sp-metric">
                <div className="sp-metric-label">Estimated wait time</div>
                <div className="sp-metric-value">
                  Routine: {fmtWeeks(vm?.routineWeeks ?? null)}
                  <span className="sp-metric-sep">•</span>
                  Urgent: {fmtWeeks(vm?.urgentWeeks ?? null)}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="sp-main">
        <div className="sp-container">
          {loading && (
            <div className="sp-stack">
              <div className="sp-card sp-skeleton" />
              <div className="sp-card sp-skeleton" />
            </div>
          )}

          {!loading && (error || !vm) && (
            <div className="sp-card sp-error">
              <div className="sp-card-title">Couldn’t load profile</div>
              <p className="sp-muted">{error || "Not found."}</p>
              {/* ✅ Minimal change */}
              <Link className="sp-btn" to={BACK_TO_SEARCH}>
                Back to search
              </Link>
            </div>
          )}

          {!loading && !error && vm && (
            <section className="sp-stack">
              {/* Referral method */}
              <div className="sp-card">
                <div className="sp-card-title">Referral method</div>

                {vm.referralMethods.length === 0 &&
                (!vm.referralForm || !vm.referralForm.hasForm) ? (
                  <p className="sp-muted">Referral method not specified yet.</p>
                ) : (
                  <>
                    {vm.referralMethods.length > 0 && (
                      <div className="sp-referral-block">
                        <div className="sp-referral-chips">
                          {vm.referralMethods.map((m) => (
                            <span
                              key={`${m.referralMethodId}-${m.code}`}
                              className="sp-chip sp-chip-referral"
                            >
                              <span className="sp-chip-ico">
                                {iconForReferralCode(m.code)}
                              </span>
                              {m.name}
                            </span>
                          ))}
                        </div>

                        {vm.referralMethods.some((m) => !!m.notes) && (
                          <div className="sp-divider-top">
                            <div className="sp-section-label">Notes</div>
                            <ul className="sp-bullets">
                              {vm.referralMethods
                                .filter((m) => !!m.notes)
                                .map((m) => (
                                  <li key={`note-${m.referralMethodId}`}>
                                    <span className="sp-muted-strong">
                                      {m.name}:
                                    </span>{" "}
                                    {m.notes}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {vm.referralForm?.hasForm && (
                      <div className="sp-divider-top">
                        <div className="sp-section-label">Referral form</div>

                        <div className="sp-form-row">
                          <div className="sp-form-meta">
                            <div className="sp-form-name">
                              {vm.referralForm.fileName || "Referral form (PDF)"}
                            </div>
                            {vm.referralForm.uploadedAt && (
                              <div className="sp-form-sub">
                                Uploaded:{" "}
                                {new Date(
                                  vm.referralForm.uploadedAt
                                ).toLocaleDateString()}
                              </div>
                            )}
                          </div>

                          <button
                            className="sp-btn"
                            onClick={handleOpenReferralForm}
                            disabled={openingForm}
                          >
                            {openingForm ? "Opening…" : "Open referral form (PDF)"}
                          </button>
                        </div>

                        {openFormError && (
                          <div className="sp-footnote">{openFormError}</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Clinic info */}
              <div className="sp-card">
                <div className="sp-card-title">Clinic information</div>

                <div className="sp-kv">
                  <div className="sp-k">Clinic</div>
                  <div className="sp-v">{vm.clinicName || "—"}</div>
                </div>

                <div className="sp-kv">
                  <div className="sp-k">Address</div>
                  <div className="sp-v">{vm.address || "—"}</div>
                </div>

                <div className="sp-kv">
                  <div className="sp-k">Phone</div>
                  <div className="sp-v">{vm.clinicPhone || "—"}</div>
                </div>

                <div className="sp-kv">
                  <div className="sp-k">Fax</div>
                  <div className="sp-v">{vm.clinicFax || "—"}</div>
                </div>

                <div className="sp-kv">
                  <div className="sp-k">Administration email</div>
                  <div className="sp-v">{vm.clinicAdminEmail || "—"}</div>
                </div>

                {vm.hospitals.length > 0 && (
                  <div className="sp-divider-top">
                    <div className="sp-section-label">Hospitals</div>
                    <ChipRow items={vm.hospitals} />
                  </div>
                )}
              </div>

              {(vm.acceptedAgeGroupsLabel ||
                vm.languages.length > 0 ||
                vm.visitTypes.length > 0 ||
                vm.coverage.length > 0) && (
                <div className="sp-card">
                  <div className="sp-card-title">Access</div>

                  {vm.acceptedAgeGroupsLabel && (
                    <>
                      <div className="sp-section-label">Accepted age groups</div>
                      <div className="sp-plain-list">{vm.acceptedAgeGroupsLabel}</div>
                    </>
                  )}

                  {vm.languages.length > 0 && (
                    <>
                      <div className="sp-section-label">Languages</div>
                      <ChipRow items={vm.languages} />
                    </>
                  )}

                  {vm.visitTypes.length > 0 && (
                    <>
                      <div className="sp-section-label">Visit types</div>
                      <ChipRow items={vm.visitTypes} />
                    </>
                  )}

                  {vm.coverage.length > 0 && (
                    <>
                      <div className="sp-section-label">Coverage</div>
                      <ChipRow items={vm.coverage} />
                    </>
                  )}
                </div>
              )}

              {(vm.acceptedConditions.length > 0 ||
                vm.excludedConditions.length > 0) && (
                <div className="sp-card">
                  <div className="sp-card-title">Clinical scope</div>

                  {vm.acceptedConditions.length > 0 && (
                    <>
                      <div className="sp-section-label">Primary focus</div>
                      <ChipRow items={vm.acceptedConditions} />
                    </>
                  )}

                  {vm.excludedConditions.length > 0 && (
                    <div className="sp-divider-top">
                      <div className="sp-section-label">Not accepting for</div>
                      <div className="sp-chips">
                        {vm.excludedConditions.map((t) => (
                          <span key={t} className="sp-chip sp-chip-warn">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {(vm.procedures.length > 0 || vm.investigations.length > 0) && (
                <div className="sp-card">
                  <div className="sp-card-title">Referral requirements</div>

                  {vm.investigations.length > 0 && (
                    <>
                      <div className="sp-section-label">Required investigations</div>
                      <ChipRow items={vm.investigations} />
                    </>
                  )}

                  {vm.procedures.length > 0 && (
                    <div className="sp-divider-top">
                      <div className="sp-section-label">Services / procedures</div>
                      <ChipRow items={vm.procedures} />
                    </div>
                  )}
                </div>
              )}

              {vm.otherNotes && (
                <div className="sp-card">
                  <div className="sp-card-title">Additional notes</div>
                  <p className="sp-muted sp-notes">{vm.otherNotes}</p>
                </div>
              )}

              <div className="sp-actions">
                {/* ✅ Minimal change */}
                <Link className="sp-btn" to={BACK_TO_SEARCH}>
                  Back to search
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default SpecialistProfilePage;
